import React, { useEffect, useMemo, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import { DataCollectionPageModel } from '../../../pages/Assessment/types';
import { TextInput } from '../../Form/TextInput';
import { Checkbox } from '../../Form/Checkbox';
import { NumberInput } from '../../Form/NumberInput';
import { DateInput } from '../../Form/DateInput';
import { Textarea } from '../../Form/Textarea';
import { SelectInput } from '../../Form/Select';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAssessment,
  postAssessment,
} from '../../../store/assessmentSingle/actions';
import { SaveAssessmentModal } from '../../SaveAssessmentModal';
import { Grid } from '../../Grid';
import { IAppState } from '../../../store';
import { getApiAssessment } from '../../../store/assessment/actions';
import { useTranslation } from 'react-i18next';
import { translateTabContentText } from 'utils/helpers/useTranslation';
import { find } from 'lodash';
import { checkAllConditionsFollow } from 'utils/helpers/useCondition';

export const fieldsConfig = {
  Text: TextInput,
  Date: DateInput,
  Checkbox: Checkbox,
  Selection: SelectInput,
  Number: NumberInput,
  Grid: Grid,
  Calculated: TextInput,
  Textarea: Textarea,
};

interface IProps {
  data: DataCollectionPageModel;
  fieldsDisabled: boolean;
  pageNum: number;
  assId: number;
  children: any;
  translations?: any;
  newData?: any;
  setNewData?: React.Dispatch<React.SetStateAction<any>>;
}

const TabContent = ({
  translations,
  data,
  fieldsDisabled,
  pageNum,
  assId,
  children,
  newData,
  setNewData,
}: IProps) => {
  const dispatch = useDispatch();
  const { Pages, ID, StatusFlag } = useSelector(
    (state: IAppState) => state.singleAssessment.data,
  ).find(ass => ass.Pages.some(p => p.ID === data.ID));
  const { errors, values } = useFormikContext();
  const [isPopupOpen, setPopupOpen] = useState(false);
  const { t } = useTranslation();

  const onSaveButtonClick = () => {
    if (data.Fields.some(f => errors[f.Template.Name])) {
      return;
    }
    setPopupOpen(true);
  };
  useEffect(() => {}, [newData]);

  const handleSaveClick = async () => {
    const dataToPost = [];
    let objEmpty = [];
    Pages.forEach(p =>
      p.Fields.forEach(f => {
        if (f.Template.FieldType.Name === 'Grid') {
          if (f.Template.Validator.MaxRows > f.GridChildren.length) {
            let lastRow = f.GridChildren[f.GridChildren.length - 1];
            if (lastRow) {
              objEmpty = [];
              let lastCeil = lastRow[lastRow?.length - 1];
              let id = lastCeil?.ID + 1;
              f.GridChildren[f.GridChildren.length - 1]?.forEach(item => {
                let newObj = {
                  ...item,
                  ID: id++,
                  Value: null,
                  GridRow: item.GridRow + 1,
                };
                objEmpty.push(newObj);
              });
            }
          }
          f.GridChildren.forEach(fieldsArray => {
            fieldsArray.forEach(field => {
              field.Value = values[f.Guid][field.GridRow][
                field.Template.Name
              ] as string | null;
            });
          });
          f.GridChildren.push(objEmpty);
          dataToPost.push({ ...f });
        } else {
          dataToPost.push({
            ...f,
            Value: values[f.Guid] as string,
          });
        }
      }),
    );
    await dispatch(postAssessment(dataToPost));
    dispatch(getAssessment(`${ID}`));
  };

  const onCloseSavePopup = () => {
    setPopupOpen(false);
  };
  return (
    <div className="tab-content">
      {children}
      <div
        className="tab-pane active"
        id="general"
        role="tabpanel"
        aria-labelledby="general-tab"
      >
        <div className="assessment-block">
          <h4 className="block-subtitle">
            {translateTabContentText(data.Template.Description, translations)}
          </h4>
          <div className="block-row">
            {data.Fields.map(field => {
              const visible = field.Template.InitiallyVisibleOnLoad;
              const noDisplayConditions =
                field.Template.DisplayConditions.length === 0 ||
                field.Template.DisplayConditions === undefined ||
                field.Template.DisplayConditions === null;
              const conditionToShow =
                visible ||
                (!visible && noDisplayConditions) ||
                (!visible &&
                  !noDisplayConditions &&
                  checkAllConditionsFollow(field, newData));
              if (fieldsConfig[field.Template.FieldType.Name]) {
                let Component;
                if (field.Template.FieldType.Name === 'Grid') {
                  return (
                    <div className={!conditionToShow ? 'hide' : ''}>
                      <Grid
                        newData={newData}
                        allFields={data}
                        translations={translations}
                        data={field}
                        key={field.Guid}
                        pageNum={pageNum}
                        assId={assId}
                      />
                    </div>
                  );
                }
                if (
                  field.Template.FieldType.Name === 'Text' &&
                  field.Template.Parameters.MultiLine
                ) {
                  Component = fieldsConfig['Textarea'];
                } else {
                  Component = fieldsConfig[field.Template.FieldType.Name];
                }
                if (field.Template.FieldType.Name === 'Selection') {
                  return (
                    <div className={!conditionToShow ? 'hide' : ''}>
                      <SelectInput
                        setNewData={setNewData}
                        newData={newData}
                        oneField={field}
                        translations={translations}
                        key={field.Guid}
                        name={field.Guid}
                        OriginalHeight={field.Template?.OriginalHeight}
                        OriginalSize={field.Template?.OriginalSize}
                        OriginalWidth={field.Template?.OriginalWidth}
                        ImageHeight={field.Template?.ImageHeight}
                        ImageWidth={field.Template?.ImageWidth}
                        imageFile={field.Template?.ImageFile}
                        MaintainAspectRatio={
                          field.Template?.MaintainAspectRatio
                        }
                        labelText={translateTabContentText(
                          field.Template.DisplayName,
                          translations,
                        )}
                        options={field.Template.Parameters.ValuesList?.Values.map(
                          o => ({
                            value: o.ID,
                            label: o.Name,
                          }),
                        )}
                        disabled={fieldsDisabled}
                        required={field.Template.Required}
                      />
                    </div>
                  );
                }
                return (
                  <div className="component-container">
                    {field.Template?.ImageFile &&
                      field.Template.FieldType.Name === 'Checkbox' && (
                        <img
                          src={`data:image/png;base64, ${field.Template?.ImageFile}`}
                          alt="img"
                        />
                      )}
                    <div className={!conditionToShow ? 'hide' : ''}>
                      <Component
                        OriginalHeight={field.Template?.OriginalHeight}
                        OriginalSize={field.Template?.OriginalSize}
                        OriginalWidth={field.Template?.OriginalWidth}
                        ImageHeight={field.Template?.ImageHeight}
                        ImageWidth={field.Template?.ImageWidth}
                        MaintainAspectRatio={
                          field.Template?.MaintainAspectRatio
                        }
                        key={field.Guid}
                        name={field.Guid}
                        labelText={translateTabContentText(
                          field.Template.DisplayName,
                          translations,
                        )}
                        imageFile={field.Template?.ImageFile}
                        toolTipText={translateTabContentText(
                          field.Template.HelpText,
                          translations,
                        )}
                        postfixText={
                          field.Template?.Parameters?.MeasureDetail?.PostfixText
                        }
                        disabled={fieldsDisabled}
                        required={field.Template.Required}
                      />
                    </div>
                  </div>
                );
              }
            })}
            <div className="save-button_wrapper">
              <button
                className="btn action-primary save-btn"
                onClick={onSaveButtonClick}
                disabled={StatusFlag === 2 || StatusFlag === 5}
              >
                {t('AP_save')}
              </button>
            </div>
          </div>
        </div>
      </div>
      <SaveAssessmentModal
        onSave={handleSaveClick}
        onExit={onCloseSavePopup}
        isOpen={isPopupOpen}
        text={t('AP_cont?')}
        cancelButtonText={t('AP_cancel')}
        primaryButtonText={t('AP_save_changes')}
      />
    </div>
  );
};
export { TabContent };
